// src/utils/messageFormatterUtil.ts
import moment from 'moment';
import logger, { Logger } from "../../../utils/logger";

/**
 * Format date for message headers
 */
export const formatDateHeader = (date: string): string => {
  const time = moment(date);
  const now = moment();
  
  if (time.format("L") === now.format("L")) {
    return "Today";
  }
  
  if (time.isSame(now.subtract(1, "days"), "day")) {
    return "Yesterday";
  }
  
  return time.format("MMMM D, YYYY");
};

/**
 * Format time for message bubbles
 */
export const formatMessageTime = (date: string): string => {
  return moment(date).format("HH:mm");
};

/**
 * Determine if a date is today, yesterday, or another date
 * Used for conversation list
 */
export const getRelativeDate = (date: string): string => {
  const inputDate = moment(date);
  
  if (inputDate.isSame(moment(), "day")) {
    return inputDate.format("hh:mm A"); // Show time if today
  }

  if (inputDate.isSame(moment().subtract(1, "days"), "day")) {
    return "Yesterday"; // Show "Yesterday" if date is yesterday
  }

  if (
    inputDate.isAfter(moment().subtract(7, "days")) &&
    !inputDate.isSame(moment(), "day") &&
    !inputDate.isSame(moment().subtract(1, "days"), "day")
  ) {
    return inputDate.format("dddd"); // Show weekday if within last week
  }

  // For dates older than a week, show the date
  return inputDate.format("MMM D, YYYY");
};

/**
 * Extract message content from various message formats
 */
export const extractMessageContent = (message: any): string => {
    if (!message) return '';
    
   
    
    // For text messages
    if (message.body && typeof message.body === 'string') {
      return message.body;
    }
    
    // For text messages with nested structure (outbound messages)
    if (message.body && typeof message.body === 'object' && message.body.text?.body) {
      return message.body.text.body;
    }
    
    // For text messages with nested structure
    if (message.text && typeof message.text.body === 'string') {
      return message.text.body;
    }
    
    // For template messages
    if (message.template && message.template.components) {
      // Extract text from template components
      const bodyComponents = message.template.components.filter(
        (c: any) => c.type === 'BODY'
      );
      if (bodyComponents.length > 0) {
        return bodyComponents[0].text || '';
      }
    }
    
    // For reaction messages
    if (message.emoji) {
      return message.emoji;
    }
    
    // For message types with media
    if (message.caption) {
      return message.caption;
    }
    
    // Try various other formats
    if (typeof message === 'string') return message;
    
    // Last resort - try to stringify (might fail if circular references)
    try {
      return JSON.stringify(message);
    } catch (e) {
      logger.error("Failed to stringify message:", e);
      return '';
    }
  };

/**
 * Group messages by date and time for display
 */
export const groupMessagesByDateTime = (messages: any[]) => {
  try {
    const groupedByDate: {
      [date: string]: {
        [time: string]: any[];
      };
    } = {};
    
    messages.forEach(message => {
      const date = moment(message.createdAt).format('L');
      const time = moment(message.createdAt).format('hh:mm a');
      
      if (!groupedByDate[date]) {
        groupedByDate[date] = {};
      }
      
      if (!groupedByDate[date][time]) {
        groupedByDate[date][time] = [];
      }
      
      groupedByDate[date][time].push(message);
    });
    
    return groupedByDate;
  } catch (error) {
    Logger.log("Error grouping messages by time:", error);
    return {};
  }
};

/**
 * Group messages by time for display
 */
export const groupMessagesByTime = (messages: any[]) => {
  try {
    return messages.reduce((acc: any, item: any) => {
      const time = moment(item.createdAt).format("hh:mm a");
      if (!acc[time]) {
        acc[time] = [];
      }
      acc[time].push(item);
      return acc;
    }, {});
  } catch (error) {
    Logger.log("Error grouping messages by time:", error);
    return {};
  }
};

/**
 * Determine if a message is outgoing (sent by current user)
 */
export const isOutgoingMessage = (message: any): boolean => {
  logger.log('🔍 isOutgoingMessage check:', {
    messageId: message._id,
    sender: message.sender,
    senderIsNull: message.sender === null,
    result: message.sender !== null
  });
  return message.sender !== null;
};

/**
 * Check if message contains media
 */
export const isMediaMessage = (message: any): boolean => {
  return ["document", "image", "video", "audio"].includes(message?.messageType || '');
};

/**
 * Get appropriate message status icon
 * Returns the JSX for the icon (as a string to be parsed)
 */
export const getMessageStatusIcon = (status: string): string => {
  switch (status) {
    case 'sent':
      return `<i className="ri-check-line" />`;
    case 'delivered':
      return `<i className="ri-check-double-line" />`;
    case 'read':
      return `<i className="ri-check-double-line read" />`;
    default:
      return '';
  }
};

/**
 * Get MIME type for a file extension
 */
export const getMimeTypeFromExtension = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase();
  
  const mimeTypes: {[key: string]: string} = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'mov': 'video/quicktime',
    '3gp': 'video/3gpp',
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'm4a': 'audio/mp4',
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'txt': 'text/plain'
  };
  
  return mimeTypes[ext || ''] || 'application/octet-stream';
};

/**
 * Check file type based on extension
 */
export const getFileType = (filename: string): 'image' | 'video' | 'audio' | 'document' => {
  const ext = filename.split('.').pop()?.toLowerCase();
  
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) {
    return 'image';
  }
  
  if (['mp4', 'webm', 'mov', '3gp', 'avi'].includes(ext || '')) {
    return 'video';
  }
  
  if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext || '')) {
    return 'audio';
  }
  
  return 'document';
};